import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import { ArrowRight, Sparkles, Star } from "lucide-react";
import { ASSETS, LINKS } from "../data/content";
import Countdown from "./Countdown";

const lines = [
  "30 Days.",
  "5 Weeks.",
  "1 Mission.",
];

const lineVariant = {
  hidden: { y: "110%" },
  show: (i) => ({
    y: "0%",
    transition: { duration: 0.9, delay: 0.15 * i, ease: [0.22, 1, 0.36, 1] },
  }),
};

const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const koalaY = useTransform(scrollYProgress, [0, 1], [0, 140]);
  const koalaRot = useTransform(scrollYProgress, [0, 1], [0, 12]);

  return (
    <section
      id="top"
      ref={ref}
      data-testid="hero-section"
      className="relative min-h-screen pt-28 md:pt-32 pb-16 overflow-hidden bg-[#f8f9fa]"
    >
      {/* soft background blobs */}
      <div className="absolute -top-24 -right-24 h-[420px] w-[420px] rounded-full bg-[#e5f7d6] blur-[80px] opacity-70" />
      <div className="absolute bottom-0 -left-32 h-[360px] w-[360px] rounded-full bg-[#e5f7d6] blur-[90px] opacity-60" />
      <div className="paper-noise absolute inset-0 opacity-[0.04] pointer-events-none" />

      <div className="relative max-w-7xl mx-auto px-5 md:px-8 grid lg:grid-cols-12 gap-10 items-center">
        {/* Left */}
        <div className="lg:col-span-7">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="inline-flex items-center gap-2 bg-white border border-black/5 rounded-full pl-2 pr-4 py-1.5 shadow-[0_8px_30px_rgb(0,0,0,0.05)] mb-6"
          >
            <span className="bg-[#58cc02] text-white text-[11px] font-extrabold uppercase tracking-wider px-2.5 py-1 rounded-full">
              For UPSC CSE
            </span>
            <span className="text-sm font-bold text-[#4b5563] flex items-center gap-1">
              <Sparkles className="h-4 w-4 text-[#58cc02]" /> Essay Advantage Club
            </span>
          </motion.div>

          <h1 className="font-display font-black text-6xl md:text-7xl lg:text-8xl tracking-tighter leading-[0.95] text-[#111827]">
            {lines.map((line, i) => (
              <span key={line} className="block overflow-hidden">
                <motion.span
                  className="block"
                  custom={i}
                  variants={lineVariant}
                  initial="hidden"
                  animate="show"
                >
                  {line}
                </motion.span>
              </span>
            ))}
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.6 }}
            className="mt-6 text-lg md:text-xl text-[#4b5563] max-w-xl leading-relaxed"
          >
            Transform your essay writing from{" "}
            <span className="font-extrabold text-[#111827]">average</span> to{" "}
            <span className="font-extrabold text-[#58cc02]">UPSC topper level</span>.
            You write, Arpita evaluates — personally.
          </motion.p>

          {/* price row */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.75 }}
            className="mt-8 flex flex-wrap items-center gap-4"
          >
            <a
              href={LINKS.enrol}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="hero-enrol-button"
              className="btn-3d group bg-[#58cc02] text-white font-extrabold text-base md:text-lg px-8 py-4 rounded-2xl uppercase tracking-wide flex items-center gap-2"
            >
              Enrol for ₹1,100
              <ArrowRight className="h-5 w-5 transition-transform duration-200 group-hover:translate-x-1" />
            </a>
            <div className="flex flex-col leading-tight">
              <span className="text-sm text-[#4b5563] font-bold">
                Others charge{" "}
                <s className="text-[#ef4444]">₹5K – ₹12K</s>
              </span>
              <span className="text-xs text-[#4b5563] flex items-center gap-1 mt-0.5">
                <Star className="h-3.5 w-3.5 fill-[#58cc02] text-[#58cc02]" />
                Same rigour. A fraction of the price.
              </span>
            </div>
          </motion.div>
        </div>

        {/* Right: floating koala */}
        <div className="lg:col-span-5 flex justify-center">
          <motion.div
            style={{ y: koalaY, rotate: koalaRot }}
            initial={{ opacity: 0, scale: 0.7 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.9, delay: 0.3, ease: [0.22, 1, 0.36, 1] }}
            className="relative"
          >
            <motion.img
              src={ASSETS.logo}
              alt="Essay Advantage Club mascot"
              className="w-[280px] md:w-[380px] lg:w-[440px] rounded-full object-cover drop-shadow-[0_30px_40px_rgba(0,0,0,0.15)]"
              animate={{ y: [0, -14, 0] }}
              transition={{ duration: 5, repeat: Infinity, ease: "easeInOut" }}
              data-testid="hero-logo"
            />
            <motion.div
              animate={{ y: [0, -10, 0], rotate: [0, 5, 0] }}
              transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
              className="absolute -top-2 -left-2 bg-white rounded-2xl px-4 py-2 shadow-lg border border-black/5 font-display font-extrabold text-[#58cc02]"
            >
              8 / 15
            </motion.div>
            <motion.div
              animate={{ y: [0, 10, 0], rotate: [0, -6, 0] }}
              transition={{ duration: 4.5, repeat: Infinity, ease: "easeInOut" }}
              className="absolute bottom-6 -right-2 bg-[#0a0a0a] text-white rounded-2xl px-4 py-2 shadow-lg font-display font-extrabold text-sm"
            >
              Report Card ↗
            </motion.div>
          </motion.div>
        </div>
      </div>

      {/* countdown */}
      <div className="relative max-w-7xl mx-auto px-5 md:px-8 mt-14 md:mt-20">
        <Countdown />
      </div>
    </section>
  );
};

export default Hero;
